import { collateralQualityTestValues } from "../../../../../constants/collateral-quality-test";
import { collateralQualityTestGroups, CollateralQualityTestValue } from "../../../../../types/amr-pipeline/enums/CollateralQualityTestValue";
import { CollateralQualityTest } from "../../../../../types/amr-pipeline/models/CollateralQualityTest";
import { apiUtils, moneyUtils } from "../../../../../utils";
import { Table } from "../../../../bidding/common/table";
import { ColumnBuilder } from "../../../../bidding/common/table/columns/column-builder/ColumnBuilder";
import { IColumnDefinition } from "../../../../bidding/common/table/types/ColumnDefinition";
import { Difference } from "../../../../../utils/differ/types";
import { DifferenceField } from "../DifferenceField";
import { DifferenceListProps, withDeletedItems } from "../withDeletedItems";
import { constants } from "../../../../../constants";
import { ImSubscriptionValue } from "../../../subscription/ImSubscriptionValue";
import { isNil } from "lodash";
import { DashboardWidget, DashboardWidgetContent, DashboardWidgetHeader } from "../../../../dashboard/widget";

interface ColumnContext {
    difference?: Difference<CollateralQualityTest>;
}

const valueFormatter = (value?: CollateralQualityTestValue) =>
    value ? collateralQualityTestValues[value] : undefined;

const covenantAndTargetFormatter = (value?: number) => {
    return isNil(value)
        ? constants.emptyPlaceholder
        : moneyUtils.money(value, true);
};

interface CollateralQualityTableItem {
    value: CollateralQualityTestValue;
    covenant?: number;
    target?: number;
}

const columnDefinitions: IColumnDefinition<CollateralQualityTableItem>[] = [
    {
        columnKey: "value",
        renderColumnHeaderContent: () => "Characteristics",
        renderColumnContent: (
            collateralQualityTest: CollateralQualityTableItem,
            { difference }: ColumnContext,
        ) => (
            <DifferenceField
                difference={difference?.value}
                formatter={valueFormatter}
                tooltip={valueFormatter(collateralQualityTest.value)}
            >
                {valueFormatter(collateralQualityTest.value)}
            </DifferenceField>
        ),
        className: "data-list-cell-md-flexible data-list-cell-change",
    },
    {
        columnKey: "covenant",
        renderColumnHeaderContent: () => "Covenant",
        renderColumnContent: (
            collateralQualityTest: CollateralQualityTableItem,
            { difference }: ColumnContext,
        ) => (
            <ImSubscriptionValue>
                <DifferenceField
                    difference={difference?.covenant}
                    formatter={covenantAndTargetFormatter}
                    tooltip={
                        covenantAndTargetFormatter(collateralQualityTest.covenant) === constants.emptyPlaceholder
                            ? null
                            : covenantAndTargetFormatter(collateralQualityTest.covenant)
                    }
                >
                    {covenantAndTargetFormatter(collateralQualityTest.covenant)}
                </DifferenceField>
            </ImSubscriptionValue>
        ),
        className: "data-list-cell-sm-02 text-right data-list-cell-change",
    },
    {
        columnKey: "target",
        renderColumnHeaderContent: () => "Target",
        renderColumnContent: (
            collateralQualityTest: CollateralQualityTableItem,
            { difference }: ColumnContext,
        ) => (
            <ImSubscriptionValue>
                <DifferenceField
                    difference={difference?.target}
                    formatter={covenantAndTargetFormatter}
                    tooltip={
                        covenantAndTargetFormatter(collateralQualityTest.target) === constants.emptyPlaceholder
                            ? null
                            : covenantAndTargetFormatter(collateralQualityTest.target)
                    }
                >
                    {covenantAndTargetFormatter(collateralQualityTest.target)}
                </DifferenceField>
            </ImSubscriptionValue>
        ),
        className: "data-list-cell-sm-01 text-right data-list-cell-change",
    },
];

function Component({
    items,
    difference,
}: DifferenceListProps<CollateralQualityTest, CollateralQualityTest>) {
    const columns = columnDefinitions.map((c) => new ColumnBuilder(c));
    const nonEmptyData = items.filter(x => x.covenant != null || x.target != null);
    const itemsHashTable = apiUtils.normalize(nonEmptyData, x => x.value, x => x);

    const renderTableGroup = (group: { title: string, values: CollateralQualityTestValue[] }) => {
        const dataItems = group.values.map(value => ({
            value,
            covenant: itemsHashTable[value]?.covenant,
            target: itemsHashTable[value]?.target,
        })).filter(x => x.covenant != null || x.target != null);

        if (!dataItems.length) return null; // render nothing if no data

        return (
            <div key={group.title} className="col-item">
                <DashboardWidget title={group.title}>
                    <DashboardWidgetHeader className="heading-wrap">
                        <h3>{group.title}</h3>
                    </DashboardWidgetHeader>
                    <DashboardWidgetContent>
                        <Table
                            className="data-list-striped"
                            columns={columns}
                            dataItems={dataItems}
                            createSecurityCustomArgs={(item: CollateralQualityTest) => {
                                const arrayItelDifference = difference?.find(
                                    ({ derivedValue }) => {
                                        return derivedValue?.value === item.value;
                                    },
                                );

                                return { difference: arrayItelDifference?.difference };
                            }}
                        />
                    </DashboardWidgetContent>
                </DashboardWidget>
            </div>
        );
    }

    return (
        <div className="covenant-summary row-items">
            {collateralQualityTestGroups.map(renderTableGroup)}
        </div>
    );
}

export const CovenantSummaryTables = withDeletedItems(Component);
